<template>
  <TaskPage class="signup-invited-page" no-back can-continue @action="nextStep()">
    <template #title>Get started</template>
    <div class="task-content">
      <LoadingIndicator v-if="loadingRef"/>
      <template v-else>
        <img v-if="sourceIconRef" :src="sourceIconRef"/>
        <template v-if="!signInRestrictedRef">
          <div>
            Thanks for visiting us from <b>{{ blockGrantRef?.sourceName }}</b>.
          </div>
          <div>
            Click Next to sign up!
          </div>
        </template>
        <div v-else-if="signInStore.unusedGrant">
          Thanks for visiting us from <b>{{ blockGrantRef?.sourceName }}</b>.
          This invitation group currently has {{ remainingTextRef }} remaining.
        </div>
        <template v-else-if="blockGrantRef">
          <div>
            Thanks for visiting us from <b>{{ blockGrantRef?.sourceName }}</b>!
            Unfortunately, there are no more spots left in this invitation group.
          </div>
          <div>
            Sign up now, and we'll get you in as soon as we can!
          </div>
        </template>
      </template>
    </div>
  </TaskPage>
</template>

<style lang="scss" scoped>
.task-content {
  flex-grow: 1;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }

  img {
    width: 120px;
  }
}
</style>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle } from '@webapp/util/AppUtil'
import { useSignInStore } from '@webapp/auth/SignInStore'
import { BlockGrant, useIdentityApi } from './IdentityApi'
import { useIdentityBlockGrantStore } from './IdentityBlockGrantStore'
import { Environment } from '@webapp/util/Environment'
import { pluralizeWithCount } from '@/util/LanguageUtil'
import TaskPage from '@/util/TaskPage.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'

const loadingRef = ref(false)
const signInRestrictedRef = ref(false)
const blockGrantRef = ref<BlockGrant>()

usePageTitle("Sign up")

const router = useRouter()
const signInStore = useSignInStore()
const identityApi = useIdentityApi()
const blockGrantStore = useIdentityBlockGrantStore()

const sourceIconRef = computed(() => blockGrantRef.value?.sourceIcon ? `${Environment.get('INTEGRATION_CONTENT_URL')}/${blockGrantRef.value.sourceIcon}` : undefined)
const remainingTextRef = computed(() => signInStore.unusedGrant > 10 ? "at least 10 spots" : pluralizeWithCount(signInStore.unusedGrant, ['spot', 'spots']))

onMounted(async () => {
  try {
    loadingRef.value = true
    blockGrantRef.value = undefined

    signInStore.couponCode = router.currentRoute.value.query.code as string
    if (signInStore.couponCode) {
      blockGrantRef.value = await blockGrantStore.getBlockGrantAsync(signInStore.couponCode)
      signInStore.unusedGrant = blockGrantRef.value ? blockGrantRef.value.allocated - blockGrantRef.value.used : 0
    }
    signInRestrictedRef.value = await identityApi.isSignInRestrictedAsync()
  }
  finally {
    loadingRef.value = false
  }

  if (!blockGrantRef.value) {
    nextStep(true)
  }
})

function nextStep(replace = false) {
  const next = "/signup"
  if (replace) {
    router.replace(next)
  } else {
    router.push(next)
  }
}
</script>
